import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, unref as _unref, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "page-shell" }
const _hoisted_2 = {
  key: 0,
  class: "error-banner"
}
const _hoisted_3 = {
  key: 1,
  class: "loading-row"
}
const _hoisted_4 = { class: "hero-top" }
const _hoisted_5 = {
  class: "hero-icon",
  "aria-hidden": "true"
}
const _hoisted_6 = { class: "hero-copy" }
const _hoisted_7 = { class: "hero-title" }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { class: "hero-stats" }
const _hoisted_10 = { class: "hero-stat" }
const _hoisted_11 = { class: "hero-stat__value" }
const _hoisted_12 = { class: "hero-stat" }
const _hoisted_13 = { class: "hero-stat__value" }
const _hoisted_14 = { class: "hero-stat" }
const _hoisted_15 = { class: "hero-stat__value" }
const _hoisted_16 = { key: 0 }
const _hoisted_17 = { class: "section-row" }
const _hoisted_18 = {
  class: "section-icon",
  "aria-hidden": "true"
}
const _hoisted_19 = { class: "section-copy" }
const _hoisted_20 = { class: "section-title" }
const _hoisted_21 = { class: "section-body" }
const _hoisted_22 = { class: "section-head" }
const _hoisted_23 = {
  class: "section-head__icon",
  "aria-hidden": "true"
}
const _hoisted_24 = { class: "list-stack" }
const _hoisted_25 = {
  class: "list-bullet",
  "aria-hidden": "true"
}
const _hoisted_26 = { class: "list-text" }
const _hoisted_27 = { class: "section-head" }
const _hoisted_28 = {
  class: "section-head__icon",
  "aria-hidden": "true"
}
const _hoisted_29 = { class: "list-stack" }
const _hoisted_30 = {
  class: "list-number",
  "aria-hidden": "true"
}
const _hoisted_31 = { class: "list-text" }

import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import AppCard from "@/shared/components/ui/AppCard.vue";
import { getCompanyDetails } from "@/modules/vendor/services/vendors.api";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import { getStatusColor } from "@/shared/utils/utils";

type CompanyAddress = {
  city: string;
  state: string;
  distance: string;
};

type CompanyItem = {
  name: string;
  id: number;
  description: string;
  application_status?: string;
  application_id?: number | null;
  address: Partial<CompanyAddress> | null;
  verified?: boolean;
  is_verified?: boolean;
  members?: number;
  members_count?: number;
  rating?: number;
  avg_rating?: number;
  established?: number | string;
  established_year?: number | string;
  about?: string;
  benefits?: string[];
  eligibility_requirements?: string[];
  membership?: {
    monthly_price?: number | string;
    yearly_price?: number | string;
    yearly_save_label?: string;
  };
  [key: string]: any;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'CompanyDetailPage',
  setup(__props) {

const route = useRoute();
const router = useRouter();

const isLoading = ref(false);
const errorMessage = ref("");
const company = ref<CompanyItem | null>(null);
const selectedPlan = ref<"monthly" | "yearly">("yearly");

function formatLocation(city?: string, state?: string) {
  const c = String(city || "").trim();
  const s = String(state || "").trim();
  if (c && s) return `${c}, ${s}`;
  return c || s || "—";
}

function formatCompactNumber(value: unknown) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return new Intl.NumberFormat(undefined).format(n);
}

function formatRating(value: unknown) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return n.toFixed(1);
}

function formatPrice(value: unknown) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `$${Math.round(n)}`;
}

function getRouteCompanyId(): number | null {
  const raw = route.params.id;
  const value = Array.isArray(raw) ? raw[0] : raw;
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
}

// Dummy placeholders (until backend provides these fields).
const DUMMY = {
  categoryLabel: "CHAMBER OF COMMERCE",
  isVerified: true,
  applicationStatusLabel: "Verified",
  statusPillClass: "status-pill--approved",
  membersCount: 4820,
  rating: 4.9,
  established: 1912,
  about:
    "This association represents members across the region. Members gain access to procurement opportunities, advocacy, and a vetted referral network.",
  benefits: [
    "Access to public & private procurement RFPs",
    "Legislative advocacy and policy representation",
    "Member directory & verified trust seal",
    "Quarterly networking summits",
    "Discounted group health & liability insurance",
  ],
  eligibility: [
    "Registered business entity (LLC, Inc., or sole proprietor)",
    "Minimum 1 year of operating history",
    "Two member references or industry endorsements",
  ],
  monthlyPrice: 49,
  yearlyPrice: 495,
  yearlySaveLabel: "Save 9%",
} as const;

const categoryLabel = computed(() => DUMMY.categoryLabel);
const isVerified = computed(() => DUMMY.isVerified);
const applicationStatusLabel = computed(() => DUMMY.applicationStatusLabel);
const statusPillClass = computed(() => DUMMY.statusPillClass);

const headquartersLabel = computed(() => {
  return formatLocation(company.value?.address?.city, company.value?.address?.state);
});

const aboutText = computed(() => {
  return DUMMY.about;
});

const membersCountLabel = computed(() => {
  return formatCompactNumber(DUMMY.membersCount);
});

const ratingLabel = computed(() => {
  return formatRating(DUMMY.rating);
});

const establishedLabel = computed(() => {
  return String(DUMMY.established);
});

const benefitItems = computed(() => {
  return [...DUMMY.benefits];
});

const eligibilityItems = computed(() => {
  return [...DUMMY.eligibility];
});

const monthlyPriceLabel = computed(() => formatPrice(DUMMY.monthlyPrice));
const yearlyPriceLabel = computed(() => formatPrice(DUMMY.yearlyPrice));
const yearlySaveLabel = computed(() => DUMMY.yearlySaveLabel);

async function loadCompany() {
  const id = getRouteCompanyId();
  if (!id) {
    company.value = null;
    errorMessage.value = "Invalid association id.";
    return;
  }

  isLoading.value = true;
  errorMessage.value = "";
  try {
    const response: any = await getCompanyDetails(id);
    const payload = response?.data ?? response;
    company.value = payload as CompanyItem;
  } catch (error) {
    console.error(error);
    errorMessage.value = "Unable to load association. Please try again.";
    company.value = null;
  } finally {
    isLoading.value = false;
  }
}

function handleApply() {

  if(company.value?.approved){
    router.push({
      name: "vendor.apply-for-company",
      params: { company_id: getRouteCompanyId() },
    });
  } else {
    router.push({
      name: "vendor.apply-company-memberships",
      params: { companyId: getRouteCompanyId() },
    });
  }
}

onMounted(() => {
  void loadCompany();
});

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_chip = _resolveComponent("v-chip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(errorMessage.value), 1))
      : _createCommentVNode("", true),
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(_component_v_progress_circular, {
            indeterminate: "",
            size: "18",
            width: "2"
          }),
          _cache[0] || (_cache[0] = _createElementVNode("span", null, "Loading association...", -1))
        ]))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
          (company.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(AppCard, { class: "hero-card" }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_4, [
                      _createElementVNode("div", _hoisted_5, [
                        _createVNode(_component_v_icon, {
                          size: "24",
                          color: "#e4572e"
                        }, {
                          default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                            _createTextVNode("mdi-bank-outline", -1)
                          ]))]),
                          _: 1
                        })
                      ]),
                      _createElementVNode("div", _hoisted_6, [
                        _cache[2] || (_cache[2] = _createElementVNode("div", { class: "hero-badge-row" }, null, -1)),
                        _createElementVNode("div", _hoisted_7, _toDisplayString(company.value.name), 1),
                        (company.value.application_status !== 'not_applied')
                          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                              _createVNode(_component_v_chip, {
                                color: _unref(getStatusColor)(company.value.application_status),
                                class: "text-capitalize",
                                label: ""
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(company.value.application_status), 1)
                                ]),
                                _: 1
                              }, 8, ["color"])
                            ]))
                          : _createCommentVNode("", true)
                      ])
                    ]),
                    _cache[7] || (_cache[7] = _createElementVNode("div", { class: "hero-divider" }, null, -1)),
                    _createElementVNode("div", _hoisted_9, [
                      _createElementVNode("div", _hoisted_10, [
                        _createElementVNode("div", _hoisted_11, _toDisplayString(membersCountLabel.value), 1),
                        _cache[3] || (_cache[3] = _createElementVNode("div", { class: "hero-stat__label" }, "Members", -1))
                      ]),
                      _createElementVNode("div", _hoisted_12, [
                        _createElementVNode("div", _hoisted_13, [
                          _createVNode(_component_v_icon, {
                            size: "14",
                            color: "#f59e0b"
                          }, {
                            default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                              _createTextVNode("mdi-star", -1)
                            ]))]),
                            _: 1
                          }),
                          _createElementVNode("span", null, _toDisplayString(ratingLabel.value), 1)
                        ]),
                        _cache[5] || (_cache[5] = _createElementVNode("div", { class: "hero-stat__label" }, "Rating", -1))
                      ]),
                      _createElementVNode("div", _hoisted_14, [
                        _createElementVNode("div", _hoisted_15, _toDisplayString(establishedLabel.value), 1),
                        _cache[6] || (_cache[6] = _createElementVNode("div", { class: "hero-stat__label" }, "Established", -1))
                      ])
                    ])
                  ]),
                  _: 1
                }),
                (company.value.application_status === 'not_applied')
                  ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                      _createVNode(AppButtonFlat, {
                        block: "",
                        "append-icon": "mdi-arrow-right",
                        onClick: handleApply
                      }, {
                        default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                          _createTextVNode("Apply Now", -1)
                        ]))]),
                        _: 1
                      })
                    ]))
                  : _createCommentVNode("", true),
                _createVNode(AppCard, { class: "section-card" }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_17, [
                      _createElementVNode("div", _hoisted_18, [
                        _createVNode(_component_v_icon, {
                          size: "18",
                          color: "#64748b"
                        }, {
                          default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                            _createTextVNode("mdi-map-marker-outline", -1)
                          ]))]),
                          _: 1
                        })
                      ]),
                      _createElementVNode("div", _hoisted_19, [
                        _cache[10] || (_cache[10] = _createElementVNode("div", { class: "section-kicker" }, "HEADQUARTERS", -1)),
                        _createElementVNode("div", _hoisted_20, _toDisplayString(headquartersLabel.value), 1)
                      ])
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(AppCard, { class: "section-card" }, {
                  default: _withCtx(() => [
                    _cache[11] || (_cache[11] = _createElementVNode("div", { class: "section-kicker" }, "ABOUT", -1)),
                    _createElementVNode("div", _hoisted_21, _toDisplayString(aboutText.value), 1)
                  ]),
                  _: 1
                }),
                _createVNode(AppCard, { class: "section-card" }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_22, [
                      _createElementVNode("div", _hoisted_23, [
                        _createVNode(_component_v_icon, {
                          size: "18",
                          color: "#e4572e"
                        }, {
                          default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                            _createTextVNode("mdi-shield-check-outline", -1)
                          ]))]),
                          _: 1
                        })
                      ]),
                      _cache[13] || (_cache[13] = _createElementVNode("div", { class: "section-head__title" }, "Member Benefits", -1))
                    ]),
                    _createElementVNode("div", _hoisted_24, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(benefitItems.value, (item, idx) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: `benefit-${idx}`,
                          class: "list-item"
                        }, [
                          _createElementVNode("div", _hoisted_25, [
                            _createVNode(_component_v_icon, {
                              size: "14",
                              color: "#e4572e"
                            }, {
                              default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                                _createTextVNode("mdi-check", -1)
                              ]))]),
                              _: 1
                            })
                          ]),
                          _createElementVNode("div", _hoisted_26, _toDisplayString(item), 1)
                        ]))
                      }), 128))
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(AppCard, { class: "section-card" }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_27, [
                      _createElementVNode("div", _hoisted_28, [
                        _createVNode(_component_v_icon, {
                          size: "18",
                          color: "#e4572e"
                        }, {
                          default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                            _createTextVNode("mdi-clipboard-text-outline", -1)
                          ]))]),
                          _: 1
                        })
                      ]),
                      _cache[16] || (_cache[16] = _createElementVNode("div", { class: "section-head__title" }, "Eligibility Requirements", -1))
                    ]),
                    _createElementVNode("div", _hoisted_29, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(eligibilityItems.value, (item, idx) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: `elig-${idx}`,
                          class: "list-item"
                        }, [
                          _createElementVNode("div", _hoisted_30, _toDisplayString(idx + 1), 1),
                          _createElementVNode("div", _hoisted_31, _toDisplayString(item), 1)
                        ]))
                      }), 128))
                    ])
                  ]),
                  _: 1
                })
              ], 64))
            : (_openBlock(), _createBlock(AppCard, {
                key: 1,
                class: "empty-card"
              }, {
                default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                  _createTextVNode(" Association not found. ", -1)
                ]))]),
                _: 1
              }))
        ], 64))
  ]))
}
}

})